import React, { useMemo } from 'react';
import { Typography, InfoIcon } from '@ag1/ui';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import {
  AlternateFlavor,
  AlternateFlavorConfig,
  Metafield
} from '@portal/processes/ShopV2/ShopV2.types';
import { vars } from '@ag1/ui/theme';
import { getMetafield } from '@portal/shared/utils/getMetafield';
import { errorMessage, flavorMessage } from './FlavorSelection.css';
import { isFlavorOutOfStock } from '../../utils/isFlavorOutOfStock';
import { USPRichText } from '../USPRichText';

// This is a mixed data source component for receiving the flavor message from Sanity or Shopify
type Flavor = AlternateFlavor | AlternateFlavorConfig;

function getUspMetafieldFromFlavor(flavor: Flavor): Metafield | null {
  // Many of our flavor variants expose metafields either on flavor.variant or on flavor directly.
  // Read from both, preferring variant-level first.
  const variantMetafields =
    'variant' in flavor
      ? (flavor as { variant?: { metafields?: Metafield[] } }).variant?.metafields
      : undefined;

  const flavorMetafields = (flavor as { metafields?: Metafield[] }).metafields;

  return (
    getMetafield(variantMetafields, 'usp_rich_text', 'custom') ??
    getMetafield(flavorMetafields, 'usp_rich_text', 'custom')
  );
}

interface FlavorMessageProps {
  flavorVariants: Flavor[];
  activeVariantId: string | null;
  outOfStockText: string;
}

export const FlavorMessage: React.FC<FlavorMessageProps> = ({
  flavorVariants,
  activeVariantId,
  outOfStockText
}) => {
  const activeFlavor = useMemo(
    () => flavorVariants.find((f) => f.id === activeVariantId) ?? null,
    [flavorVariants, activeVariantId]
  );

  if (!activeFlavor) return null;

  const isOutOfStock = isFlavorOutOfStock(activeFlavor);
  const message =
    typeof activeFlavor.flavorMessage === 'string'
      ? activeFlavor.flavorMessage
      : ((activeFlavor as { flavorMessage?: unknown }).flavorMessage as
          | any[] // PortableText value usually is an array of blocks; keep wide to match
          | null
          | undefined);

  const uspMf = getUspMetafieldFromFlavor(activeFlavor);

  if (isOutOfStock) {
    return (
      <Typography color="status-error" variant="body" size="2" noPadding className={errorMessage}>
        <InfoIcon color={vars.colors.text.error} />
        {outOfStockText || 'This flavor is temporarily out of stock'}
      </Typography>
    );
  }

  if (message || uspMf) {
    return (
      <div className={flavorMessage}>
        {message && typeof message === 'string' && !uspMf && <em>{message}</em>}
        {/* message is from sanity, uspMf is from shopify -- we only want to render 1 */}
        {message && typeof message !== 'string' && !uspMf && <CustomPortableText value={message} />}
        {uspMf && <USPRichText metafield={uspMf} />}
      </div>
    );
  }

  return null;
};
